import { Tabs, TabsRootProps } from '@chakra-ui/react';
import { forwardRef, ReactNode } from 'react';
import { MTabsContext } from './MTabsContext';

/*
 * `colorScheme` is omitted so v2-style callers fail tsc: in v3 it's the CSS
 * `color-scheme` property, and no Tabs variant (v2 or v3) reads a palette —
 * `pill`'s indigo is hard-coded in the recipe.
 */
export interface MTabsProps extends Omit<
  TabsRootProps,
  | 'value'
  | 'defaultValue'
  | 'onValueChange'
  | 'fitted'
  | 'onChange'
  | 'colorScheme'
> {
  index?: number;
  defaultIndex?: number;
  onChange?: (index: number) => void;
  isFitted?: boolean;
  isLazy?: boolean;
  children?: ReactNode;
}

/*
 * v3's `Tabs.Root` is value-string-based with no positional child-cloning
 * (v2's `Tabs`/`TabList`/`Tab` cloned children to inject `isSelected` based
 * on a numeric `index`). This wrapper keeps v2's `index`/`defaultIndex`/
 * `onChange(index)` shape — `MTabList`/`MTabPanels` (siblings, not props of
 * this component) independently assign each child a `value` from its own
 * position, which lines up with this root's numeric index since both lists
 * are ordered identically, matching v2's implicit contract.
 *
 * `isLazy` has no direct v3 Root-level equivalent (`Tabs.Content` takes
 * `lazyMount`/`unmountOnExit` per-panel) — threaded to `MTabPanel` via
 * context since it's set once here but consumed several levels down inside
 * `MTabPanels`.
 */
export const MTabs = forwardRef<HTMLDivElement, MTabsProps>(
  ({ index, defaultIndex, onChange, isFitted, isLazy, ...rest }, ref) => (
    <MTabsContext.Provider value={{ isLazy }}>
      <Tabs.Root
        ref={ref}
        value={index !== undefined ? String(index) : undefined}
        // v2 selected the first tab when no index was given; zag's default is
        // `null`, which selects nothing and hides every panel.
        defaultValue={String(defaultIndex ?? 0)}
        onValueChange={
          onChange ? ({ value }) => onChange(Number(value)) : undefined
        }
        fitted={isFitted}
        {...rest}
      />
    </MTabsContext.Provider>
  ),
);

MTabs.displayName = 'MTabs';

export default MTabs;
